import {useRef} from 'react';
import * as React from 'react';
import classNames from 'classnames';
import {Text} from '@shopify/polaris';
import {useShopLocalesAndMarkets} from 'utilities/use-shop-locales-and-markets';
import {useUrlParams} from 'utilities/use-url-params';
import {useSearch} from 'features/Translations/hooks';

import type {
  TranslatableField,
  TranslatableResourceLayoutFormProps,
} from '../../../types';
import {TableCellField} from '../../../TableCellField';

import styles from './RowCellMarkup.scss';

export const RowCellMarkup = React.memo(function RowCellMarkup({
  resourceId,
  fieldKey,
  gidKey,
  label,
  value,
  reference,
  type,
  translatableResourceType,
  setField,
  display,
  metafieldDetails,
  error,
  disabled,
  defaultContentLocaleName,
  translatedContentLocaleName,
  isOnlyMarketLocalizable,
  status,
  dirty,
  fileReference,
  group,
}: TranslatableField & {
  group?: {
    currentIndex: number;
    size: number;
  };
  setField: TranslatableResourceLayoutFormProps['setField'];
  disabled: boolean;
  defaultContentLocaleName: string;
  translatedContentLocaleName: string;
}) {
  const readOnlyFocusRef = useRef<HTMLDivElement>(null);
  const focusRef = useRef<HTMLDivElement>(null);
  const {currentMarket} = useShopLocalesAndMarkets();
  const {selected} = useSearch();
  const {urlParams} = useUrlParams();

  const showDefaultContentLocaleName = Boolean(currentMarket);

  const handleFocus = (focusRef: React.RefObject<HTMLDivElement>) => {
    if (!focusRef.current) return;

    const inputFocusRef = focusRef.current.querySelector('input');
    const textareaFocusRef = focusRef.current.querySelector('textarea');

    if (inputFocusRef) {
      inputFocusRef.focus();
    } else if (textareaFocusRef) {
      textareaFocusRef.focus();
    }
  };
  const defaultContentLabel = isOnlyMarketLocalizable
    ? label
    : `${label} - ${defaultContentLocaleName}`;
  const translatedContentLabel = isOnlyMarketLocalizable
    ? `${label} - ${currentMarket?.name}`
    : `${label} - ${translatedContentLocaleName}`;

  const currentGroupIndex = (group?.currentIndex || 0) + 1;
  const groupSize = group?.size || 0;
  const firstRowInGroup = !(group?.currentIndex && group.currentIndex > 0);

  const gidKeyFromSelected =
    urlParams.highlight && selected?.length
      ? selected[0].substring(0, selected[0].lastIndexOf('/'))
      : undefined;

  return (
    <tr
      className={classNames(
        styles.Row,
        gidKeyFromSelected === gidKey && styles.RowActive,
      )}
    >
      {firstRowInGroup && (
        <th
          className={styles.Row__Header}
          scope="row"
          rowSpan={group?.size || 1}
          style={
            groupSize
              ? {
                  gridRow: `span ${groupSize}`,
                }
              : undefined
          }
        >
          {label}
          <p className={styles.Subtitle}>
            {groupSize > 1
              ? display.subtitle?.plural
              : display.subtitle?.singular}
          </p>
        </th>
      )}
      <td
        className={classNames(styles.RowCell, styles.RowCell__disabled)}
        onClick={() => handleFocus(readOnlyFocusRef)}
      >
        {showDefaultContentLocaleName && (
          <div className={styles.LocalizedLocaleNameHeader}>
            <Text as="span" variant="bodySm" tone="subdued">
              {defaultContentLocaleName}
            </Text>
          </div>
        )}
        <TableCellField
          ref={readOnlyFocusRef}
          resourceId={resourceId}
          fieldKey={fieldKey}
          type={type}
          translatableResourceType={translatableResourceType}
          gidKey={gidKey}
          label={defaultContentLabel}
          reference={reference}
          value={reference}
          dirty={false}
          setField={() => {}}
          display={display}
          metafieldDetails={metafieldDetails}
          disabled={disabled}
          fileReference={fileReference}
          readOnly
        />
      </td>
      <td
        className={classNames(
          styles.RowCell,
          disabled && styles.RowCell__disabled,
          groupSize && currentGroupIndex && currentGroupIndex < groupSize
            ? styles.RowCell__mobileInnerHeader
            : undefined,
        )}
        onClick={() => handleFocus(focusRef)}
      >
        <div className={styles.LocalizedLocaleNameHeader}>
          <Text as="span" variant="bodySm" tone="subdued">
            {translatedContentLocaleName}
          </Text>
        </div>
        <TableCellField
          ref={focusRef}
          resourceId={resourceId}
          fieldKey={fieldKey}
          type={type}
          translatableResourceType={translatableResourceType}
          gidKey={gidKey}
          label={translatedContentLabel}
          reference={reference}
          value={value}
          dirty={dirty}
          setField={disabled ? () => {} : setField}
          display={display}
          metafieldDetails={metafieldDetails}
          error={error}
          disabled={disabled}
          status={status}
          fileReference={fileReference}
        />
      </td>
    </tr>
  );
});
